import type { AiCreatorEvent } from '@innermate/shared';
import { parseSseEventBlock } from './sse-parse';

export interface CreateSkillWithAiCallbacks {
  onProgress?: (message: string) => void;
  onIterationStart?: (ev: { iter: number; limit: number }) => void;
  onTextDelta?: (text: string) => void;
  onToolCallStart?: (ev: { toolCallId: string; toolName: string; argsPreview: string }) => void;
  onToolCallDone?: (ev: {
    toolCallId: string;
    toolName: string;
    status: 'ok' | 'error';
    summary: string;
  }) => void;
  onHeartbeat?: (ev: { elapsedSec: number }) => void;
  signal?: AbortSignal;
}

export interface CreateSkillWithAiResult {
  skillId: string;
  slug: string;
}

/**
 * SSE 로 Skill Creator 호출. 완료 시 { skillId, slug } 반환.
 * 중간 이벤트는 callbacks 로 흘려보낸다.
 */
export async function createSkillWithAi(
  prompt: string,
  callbacks: CreateSkillWithAiCallbacks = {},
): Promise<CreateSkillWithAiResult> {
  const res = await fetch('/api/admin/skills/create-with-ai', {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ prompt }),
    signal: callbacks.signal,
  });

  if (!res.ok || !res.body) {
    let message = `요청 실패 (${res.status})`;
    try {
      const text = await res.text();
      const json = text ? JSON.parse(text) : null;
      if (json?.error?.message) message = json.error.message;
    } catch {
      // ignore
    }
    throw new Error(message);
  }

  const reader = res.body.pipeThrough(new TextDecoderStream()).getReader();
  let buf = '';
  let result: CreateSkillWithAiResult | null = null;

  while (true) {
    const { value, done: finished } = await reader.read();
    if (finished) break;
    if (value) buf += value;
    const events = buf.split('\n\n');
    buf = events.pop() ?? '';
    for (const ev of events) {
      const json = parseSseEventBlock(ev);
      if (!json) continue;
      let payload: AiCreatorEvent;
      try {
        payload = JSON.parse(json) as AiCreatorEvent;
      } catch {
        continue;
      }
      if (payload.t === 'progress') {
        callbacks.onProgress?.(payload.message);
      } else if (payload.t === 'iteration_start') {
        callbacks.onIterationStart?.({ iter: payload.iter, limit: payload.limit });
      } else if (payload.t === 'text_delta') {
        callbacks.onTextDelta?.(payload.v);
      } else if (payload.t === 'tool_call_start') {
        callbacks.onToolCallStart?.({
          toolCallId: payload.toolCallId,
          toolName: payload.toolName,
          argsPreview: payload.argsPreview,
        });
      } else if (payload.t === 'tool_call_done') {
        callbacks.onToolCallDone?.({
          toolCallId: payload.toolCallId,
          toolName: payload.toolName,
          status: payload.status,
          summary: payload.summary,
        });
      } else if (payload.t === 'heartbeat') {
        callbacks.onHeartbeat?.({ elapsedSec: payload.elapsedSec });
      } else if (payload.t === 'done') {
        result = { skillId: payload.skillId, slug: payload.slug };
      } else if (payload.t === 'error') {
        throw new Error(payload.message);
      }
    }
  }

  if (!result) throw new Error('스트림이 정상 종료되지 않았습니다.');
  return result;
}
